import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PERIODS } from "@/pages/finance/MonthlyBatch";
import { LayoutDashboard, TrendingUp, TrendingDown, Wallet, AlertTriangle, Receipt } from "lucide-react";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR")} Dhs`;

const TONES = {
  slate: { icon: "text-slate-500", val: "text-slate-700" },
  sky: { icon: "text-sky-500", val: "text-sky-700" },
  emerald: { icon: "text-emerald-500", val: "text-emerald-700" },
  amber: { icon: "text-amber-500", val: "text-amber-700" },
  red: { icon: "text-red-500", val: "text-red-700" },
  rose: { icon: "text-rose-500", val: "text-rose-700" },
  indigo: { icon: "text-indigo-500", val: "text-indigo-700" },
};

const KPI = ({ label, value, sub, icon: Icon, tone = "slate", testid }) => (
  <Card className="card-float border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-slate-500 text-sm">{label}</div>
      <Icon className={`h-5 w-5 ${TONES[tone].icon}`} />
    </div>
    <div className={`text-2xl font-bold mt-1 ${TONES[tone].val}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </Card>
);

export default function FinanceDashboard() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const canView = hasRole("super_admin", "direction", "administration", "finance");
  const [d, setD] = useState(null);
  const [years, setYears] = useState([]);
  const [syid, setSyid] = useState("all");
  const [period, setPeriod] = useState("all");

  useEffect(() => { api.get("/finance/school-years").then((r) => setYears(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = {};
    if (syid !== "all") params.school_year_id = syid;
    if (period !== "all") params.period_label = period;
    api.get("/finance/dashboard", { params }).then((r) => setD(r.data)).catch(() => {});
  }, [syid, period]);
  if (!canView) return <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div>;

  return (
    <div data-testid="finance-dashboard-page">
      <div className="flex items-center gap-3 mb-4"><LayoutDashboard className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("financeDashboard")}</h1></div>
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="dash-filters">
        <Select value={syid} onValueChange={setSyid}><SelectTrigger className="w-48" data-testid="dash-year"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allSchoolYears")}</SelectItem>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent></Select>
        <Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-48" data-testid="dash-period"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allMonths")}</SelectItem>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Button variant="ghost" size="sm" data-testid="dash-reset" onClick={() => { setSyid("all"); setPeriod("all"); }}>{t("resetFilters")}</Button>
        <span className="text-sm text-slate-400 ml-auto" data-testid="dash-selected">{t("selectedPeriod")}: {period === "all" ? t("allMonths") : period}</span>
      </div>
      {!d ? <p className="text-slate-400 py-10 text-center">…</p> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <KPI testid="kpi-facture" label={t("caFactureP")} value={money(d.ca_facture)} icon={Receipt} tone="sky" />
            <KPI testid="kpi-encaisse" label={t("caEncaisseP")} value={money(d.ca_encaisse)} icon={TrendingUp} tone="emerald" />
            <KPI testid="kpi-reste" label={t("resteADate")} value={money(d.reste_a_encaisser)} sub={`${d.factures_impayees} ${t("unpaidInvoices")} (${t("aDate")})`} icon={AlertTriangle} tone="amber" />
            <KPI testid="kpi-retard" label={`${t("totalOverdue")} (${t("aDate")})`} value={money(d.total_retard)} sub={`${d.familles_retard} ${t("overdueFamilies")} · ${d.eleves_retard} ${t("overdueStudents")}`} icon={AlertTriangle} tone="red" />
          </div>
          <p className="text-xs text-slate-400 mb-3" data-testid="dash-help">{t("collectHelp")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <KPI testid="kpi-depenses" label={t("depensesP")} value={money(d.depenses)} icon={TrendingDown} tone="rose" />
            <KPI testid="kpi-caisses" label={`${t("soldeCaisses")} (${t("aDate")})`} value={money(d.solde_caisses)} icon={Wallet} tone="indigo" />
            <KPI testid="kpi-net" label={t("resultatNetP")} value={money(d.resultat_net)} icon={d.resultat_net >= 0 ? TrendingUp : TrendingDown} tone={d.resultat_net >= 0 ? "emerald" : "red"} />
          </div>
          <Card className="card-float border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-800 mb-3">{t("cashAccounts")}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.cash_accounts.map((a) => (
                <button key={a.id} onClick={() => navigate("/finance/cash-accounts")} className="text-left rounded-lg border border-slate-200 p-3 hover:border-primary transition-colors" data-testid={`dash-cash-${a.id}`}>
                  <div className="text-slate-500 text-sm">{a.name}</div>
                  <div className="text-xl font-bold text-slate-900">{money(a.balance)}</div>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
